/* =============================================================
   usePartnerList.js — Partner university list with Supabase
   -------------------------------------------------------------
   Fetches partner universities from the Supabase
   "partner_universities" table and exposes addPartner /
   addPartners to insert new rows.

   Usage:
     const { partners, loading, error, addPartner, addPartners }
       = usePartnerList();

   Data flow:
     1. On mount, fetches all partner_universities (RLS filters
        automatically by university — only ESMT's partners).
     2. Fetches student counts per partner in a second query
        (students with a non-null partner_id). Joined in JS.
     3. Each DB row is mapped to the shape PartnersList expects
        (see toUIShape below — DB column names → UI field names).
     4. addPartner / addPartners INSERT into Supabase and refetch.

   TAB-FREEZE RESILIENCE:
     AbortController + 8-second timeout on every fetch.
     visibilitychange listener refetches when the tab regains focus.

   ✓ DONE (backend integration):
   - Removed BASE_PARTNERS mock seed data
   - Removed localStorage persistence
   - Replaced with Supabase SELECT + INSERT
   - Student count per partner computed from students table
   - university_id taken from AuthContext (no hardcoding)
   ============================================================= */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';

const FETCH_TIMEOUT_MS = 8000;

/* ─── CONNECTION STATUS STYLES ───────────────────────────────── */
const CONN_STYLES = {
  'Connected': { connStyle: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  '—':         { connStyle: 'bg-slate-100 text-slate-400',     dot: ''               },
};


/* ─── toUIShape ──────────────────────────────────────────────── */
function toUIShape(row, studentCount) {
  const conn  = row.connection === 'Connected' ? 'Connected' : '—';
  const style = CONN_STYLES[conn];

  return {
    id:                  row.id,
    abbr:                row.abbreviation,
    name:                row.name,
    country:             row.country,
    type:                row.type,
    students:            studentCount,
    max:                 row.max_students,
    connection:          conn,
    connStyle:           style.connStyle,
    dot:                 style.dot,
    action:              conn === 'Connected' ? 'View' : 'Invite',
    email:               row.contact_email ?? '',
    linked_university_id: row.linked_university_id ?? null,
    key_dates:           row.key_dates      ?? [],
    useful_links:        row.useful_links   ?? [],
    contact_emails:      row.contact_emails ?? [],
  };
}


/* ─── usePartnerList HOOK ────────────────────────────────────── */
export function usePartnerList() {
  const { user } = useAuth();

  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const abortRef = useRef(null);


  /* ── fetchPartners ─────────────────────────────────────────── */
  const fetchPartners = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    const timeoutId = setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setError(null);

    try {
      const [partnerRes, countRes] = await Promise.all([
        supabase
          .from('partner_universities')
          .select('*')
          .order('name', { ascending: true })
          .abortSignal(signal),
        supabase
          .from('students')
          .select('partner_id')
          .not('partner_id', 'is', null)
          .eq('archived', false)
          .abortSignal(signal),
      ]);

      if (signal.aborted) return;

      if (partnerRes.error) {
        setError(partnerRes.error.message);
        return;
      }

      const countMap = new Map();
      for (const row of (countRes.data ?? [])) {
        countMap.set(row.partner_id, (countMap.get(row.partner_id) ?? 0) + 1);
      }

      setPartners(
        (partnerRes.data ?? []).map(row => toUIShape(row, countMap.get(row.id) ?? 0))
      );
    } catch (err) {
      if (signal.aborted) return;
      setError(err?.message ?? 'Failed to load partners');
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);


  /* ── initial load ──────────────────────────────────────────── */
  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  /* ── Refetch when the user switches back to this tab ─────────── */
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchPartners();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchPartners]);

  /* ── Abort on unmount ────────────────────────────────────────── */
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);


  /* ── addPartner ────────────────────────────────────────────── */
  const addPartner = async (formData) => {
    if (!user?.universityId) throw new Error('Not authenticated');

    const row = {
      university_id:        user.universityId,
      name:                 formData.name.trim(),
      abbreviation:         (formData.abbr || formData.name.split(' ').map(w => w[0]).join('')).toUpperCase().slice(0, 3),
      country:              formData.country,
      type:                 formData.type   || 'BILATERAL',
      connection:           formData.linkedUniversityId ? 'Connected'
                              : formData.connection === 'Connected' ? 'Connected' : 'Manual',
      max_students:         10,
      contact_email:        formData.email?.trim().toLowerCase() ?? null,
      linked_university_id: formData.linkedUniversityId ?? null,
    };

    const { error: insertError } = await supabase
      .from('partner_universities')
      .insert(row);

    if (insertError) throw insertError;

    await fetchPartners();
  };


  /* ── addPartners ───────────────────────────────────────────── */
  // Smart upsert: if a stub with the same name already exists (created
  // automatically during a student import), UPDATE it with the full data
  // instead of creating a duplicate. Students linked to the stub keep
  // their partner_id and are now connected to the complete profile.
  const addPartners = async (dataArray) => {
    if (!user?.universityId) throw new Error('Not authenticated');

    // Fetch existing partners once to detect stubs by name
    const { data: existing } = await supabase
      .from('partner_universities')
      .select('id, name')
      .eq('university_id', user.universityId);

    const existingMap = new Map(
      (existing ?? []).map(p => [p.name.toLowerCase().trim(), p.id])
    );

    const toInsert = [];
    const toUpdate = []; // [{ id, data }]

    for (const formData of dataArray) {
      const row = {
        university_id:           user.universityId,
        name:                    formData.name.trim(),
        abbreviation:            (formData.abbr || formData.name.split(' ').map(w => w[0]).join('')).toUpperCase().slice(0, 3),
        country:                 formData.country    || '',
        type:                    formData.type       || 'BILATERAL',
        connection:              formData.linkedUniversityId ? 'Connected'
                                   : formData.connection === 'Connected' ? 'Connected' : 'Manual',
        max_students:            10,
        contact_email:           formData.email?.trim().toLowerCase() ?? null,
        linked_university_id:    formData.linkedUniversityId ?? null,
        contact_emails:          formData.contact_emails ?? [],
        exchange_period:         formData.exchange_period         ?? null,
        campus:                  formData.campus                  ?? null,
        business_school:         formData.business_school         ?? null,
        language_of_instruction: formData.language_of_instruction ?? null,
        useful_links:            formData.useful_links            ?? [],
        key_dates:               formData.key_dates               ?? [],
      };

      const existingId = existingMap.get(formData.name.toLowerCase().trim());
      if (existingId) {
        toUpdate.push({ id: existingId, data: row });
      } else {
        toInsert.push(row);
      }
    }

    // Bulk insert new partners
    if (toInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('partner_universities')
        .insert(toInsert);
      if (insertError) throw insertError;
    }

    // Update existing stubs with full data (university_id excluded from patch)
    for (const { id, data } of toUpdate) {
      const { university_id: _omit, ...patch } = data;
      const { error: updateError } = await supabase
        .from('partner_universities')
        .update(patch)
        .eq('id', id);
      if (updateError) throw updateError;
    }

    await fetchPartners();
  };


  /* ── deletePartner ─────────────────────────────────────────── */
  const deletePartner = async (id) => {
    const { error: deleteError } = await supabase
      .from('partner_universities')
      .delete()
      .eq('id', id);

    if (deleteError) throw deleteError;

    await fetchPartners();
  };

  /* ── deleteAllPartners ─────────────────────────────────────── */
  const deleteAllPartners = async () => {
    if (!user?.universityId) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('partner_universities')
      .delete()
      .eq('university_id', user.universityId);

    if (deleteError) throw deleteError;

    await fetchPartners();
  };


  return { partners, loading, error, addPartner, addPartners, deletePartner, deleteAllPartners };
}
